import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Truck, Clock, CheckCircle, Loader2, Wallet, BarChart3 } from 'lucide-react';

export default function MyEarnings({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats/my-earnings')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load earnings'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-center text-slate-500 py-12">No earnings data available.</p>;

  const statCards = [
    { label: "Today's Revenue", value: `$${data.today.gross.toLocaleString()}`, sub: `${data.today.loads} loads`, color: 'blue' },
    { label: "This Week", value: `$${data.week.gross.toLocaleString()}`, sub: `${data.week.loads} loads`, color: 'indigo' },
    { label: "My Commission Earned", value: `$${data.all_time.earned.toLocaleString()}`, sub: `${data.commission.dispatch_fee_pct}% fee, your split`, color: 'green' },
    { label: "Pending Payout", value: `$${data.payouts.pending.toLocaleString()}`, sub: 'Awaiting payment', color: 'amber' },
    { label: "Paid Out", value: `$${data.payouts.paid.toLocaleString()}`, sub: 'Total received', color: 'emerald' },
    { label: "Active Loads", value: data.active_loads, sub: `${data.avg_rate_per_mile > 0 ? `$${data.avg_rate_per_mile}/mi avg` : 'No mileage data'}`, color: 'purple' },
  ];

  const colorMap = {
    blue: 'border-l-blue-500', indigo: 'border-l-indigo-500', green: 'border-l-green-500',
    amber: 'border-l-amber-500', emerald: 'border-l-emerald-500', purple: 'border-l-purple-500'
  };
  const textMap = {
    blue: 'text-blue-600', indigo: 'text-indigo-600', green: 'text-green-600',
    amber: 'text-amber-600', emerald: 'text-emerald-600', purple: 'text-purple-600'
  };

  return (
    <div className="space-y-6" data-testid="my-earnings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">My Earnings</h1>
        <p className="text-slate-600 mt-1">Your commission: {data.commission.dispatch_fee_pct}% dispatch fee, {data.commission.factoring_pct}% factoring, 50/50 split on remaining</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((s, i) => (
          <Card key={i} className={`border-l-4 ${colorMap[s.color]}`} data-testid={`earnings-stat-${i}`}>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{s.label}</p>
              <p className={`text-2xl font-bold mt-1 ${textMap[s.color]}`}>{s.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Commission Breakdown */}
      <Card data-testid="commission-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="w-5 h-5 text-green-600" />
            How Your Commission Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
            <div className="flex justify-between"><span className="text-slate-600">Total Dispatch Fee:</span><span className="font-bold">{data.commission.dispatch_fee_pct}%</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Factoring Company Gets:</span><span className="font-bold text-red-600">-{data.commission.factoring_pct}%</span></div>
            <div className="border-t pt-2 flex justify-between"><span className="text-slate-600">Remaining to Split:</span><span className="font-bold">{(data.commission.dispatch_fee_pct - data.commission.factoring_pct).toFixed(1)}%</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Urbanvision Gets (50%):</span><span className="font-bold text-blue-600">{((data.commission.dispatch_fee_pct - data.commission.factoring_pct) / 2).toFixed(1)}%</span></div>
            <div className="flex justify-between"><span className="text-slate-600">You Get (50%):</span><span className="font-bold text-green-600">{((data.commission.dispatch_fee_pct - data.commission.factoring_pct) / 2).toFixed(1)}%</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Table */}
      <Card data-testid="performance-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Load Performance ({data.performance.length} loads)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.performance.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No loads booked yet. Start booking to see your earnings!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="earnings-table">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500 uppercase">
                    <th className="pb-2 pr-3">Load #</th>
                    <th className="pb-2 pr-3">Gross</th>
                    <th className="pb-2 pr-3">Fee %</th>
                    <th className="pb-2 pr-3">Factoring</th>
                    <th className="pb-2 pr-3 text-green-700">You Earned</th>
                    <th className="pb-2 pr-3 text-blue-700">Company</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.performance.map((p, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`perf-row-${i}`}>
                      <td className="py-2 pr-3 font-medium">{p.load_number}</td>
                      <td className="py-2 pr-3">${p.gross.toLocaleString()}</td>
                      <td className="py-2 pr-3">{p.dispatch_fee_pct}%</td>
                      <td className="py-2 pr-3 text-red-600">${p.factoring_amount.toFixed(2)}</td>
                      <td className="py-2 pr-3 font-bold text-green-700">${p.agent_earned.toFixed(2)}</td>
                      <td className="py-2 pr-3 text-blue-700">${p.company_earned.toFixed(2)}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                          p.status === 'completed' || p.status === 'dropped_off' ? 'bg-green-100 text-green-700' :
                          p.status === 'booked' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{p.status.replace('_', ' ')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
